/**
 * The sidebar, on a phone.
 *
 * Below `md` there is no room for a permanent 268px column beside a readable
 * measure, so the list becomes an overlay drawer. It is a Radix dialog rather
 * than a hand-rolled panel because the drawer needs a focus trap, an escape
 * key, an inert background and a scrim — all of which are easy to get subtly
 * wrong and none of which are worth re-implementing.
 *
 * Picking a thread closes it. On a narrow screen the drawer covers the thing
 * you just asked to read, so leaving it open would only ever mean one extra
 * dismissing tap.
 */

import { Plus } from "lucide-react";
import type { Conversation } from "../../shared/types.ts";
import { Button } from "./ui/button.tsx";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "./ui/sheet.tsx";
import { ThreadNav } from "./ThreadNav.tsx";

interface SidebarSheetProps {
  readonly conversations: readonly Conversation[];
  readonly activeId: string | null;
  readonly onSelect: (id: string) => void;
  readonly onCreate: () => void;
  readonly onDelete: (id: string) => void;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
}

export function SidebarSheet({
  conversations,
  activeId,
  onSelect,
  onCreate,
  onDelete,
  open,
  onOpenChange,
}: SidebarSheetProps) {
  const close = () => onOpenChange(false);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="left"
        className="w-[min(20rem,85vw)] gap-3 border-sidebar-border bg-sidebar p-3 font-sans text-sidebar-foreground md:hidden"
      >
        <SheetHeader className="gap-1 p-0">
          <SheetTitle className="font-serif text-xl font-normal">
            sZero
          </SheetTitle>
          <SheetDescription className="sr-only">
            Your saved conversations.
          </SheetDescription>
        </SheetHeader>

        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            onCreate();
            close();
          }}
          className="shrink-0 border-border-control bg-sidebar-accent"
        >
          <Plus />
          New question
        </Button>

        <ThreadNav
          conversations={conversations}
          activeId={activeId}
          onSelect={(id) => {
            onSelect(id);
            close();
          }}
          onDelete={onDelete}
        />
      </SheetContent>
    </Sheet>
  );
}
